/**
 * 微信小程序场景值定义
 * @see https://developers.weixin.qq.com/miniprogram/dev/reference/scene-list.html
 */

/** 场景值类型定义 */
export type WechatMpScene = {
  /** 场景值ID */
  id: number;
  /** 场景值说明 */
  description: string;
  /** 是否已废弃 */
  deprecated?: boolean;
};

/** 场景值分类 */
export enum WechatMpSceneCategory {
  /** 搜索相关 */
  SEARCH = 'search',
  /** 小程序分享/消息 */
  SHARE = 'share',
  /** 扫码打开 */
  SCAN = 'scan',
  /** 公众号相关 */
  OFFICIAL_ACCOUNT = 'officialAccount',
  /** 微信钱包/支付 */
  PAYMENT = 'payment',
  /** 系统相关 */
  SYSTEM = 'system',
  /** 广告相关 */
  AD = 'ad',
  /** 视频号相关 */
  VIDEO_CHANNELS = 'videoChannels',
  /** 企业微信相关 */
  WORK_WEIXIN = 'workWeixin',
  /** 其他 */
  OTHERS = 'others',
}

/** 微信小程序场景值配置 */
export const wechatMpSceneConfig: Record<number, WechatMpScene> = {
  1000: { id: 1000, description: '其他' },
  1001: { id: 1001, description: '发现页小程序「最近使用」列表' },
  1005: { id: 1005, description: '微信首页顶部搜索框的搜索结果页' },
  1006: { id: 1006, description: '发现栏小程序主入口搜索框的搜索结果页' },
  1007: { id: 1007, description: '单人聊天会话中的小程序消息卡片' },
  1008: { id: 1008, description: '群聊会话中的小程序消息卡片' },
  1010: { id: 1010, description: '收藏夹' },
  1011: { id: 1011, description: '扫描二维码' },
  1012: { id: 1012, description: '长按图片识别二维码' },
  1013: { id: 1013, description: '扫描手机相册中选取的二维码' },
  1014: { id: 1014, description: '小程序订阅消息' },
  1017: { id: 1017, description: '前往小程序体验版的入口页' },
  1019: { id: 1019, description: '微信钱包' },
  1020: { id: 1020, description: '公众号 profile 页相关小程序列表', deprecated: true },
  1022: { id: 1022, description: '聊天顶部置顶小程序入口', deprecated: true },
  1023: { id: 1023, description: '安卓系统桌面图标' },
  1024: { id: 1024, description: '小程序 profile 页' },
  1025: { id: 1025, description: '扫描一维码' },
  1026: { id: 1026, description: '发现栏小程序主入口，「附近的小程序」列表' },
  1027: { id: 1027, description: '微信首页顶部搜索框搜索结果页「使用过的小程序」列表' },
  1028: { id: 1028, description: '我的卡包' },
  1029: { id: 1029, description: '小程序中的卡券详情页' },
  1030: { id: 1030, description: '自动化测试下打开小程序' },
  1031: { id: 1031, description: '长按图片识别一维码' },
  1032: { id: 1032, description: '扫描手机相册中选取的一维码' },
  1034: { id: 1034, description: '微信支付完成页' },
  1035: { id: 1035, description: '公众号自定义菜单' },
  1036: { id: 1036, description: 'App 分享消息卡片' },
  1037: { id: 1037, description: '小程序打开小程序' },
  1038: { id: 1038, description: '从另一个小程序返回' },
  1039: { id: 1039, description: '摇电视' },
  1042: { id: 1042, description: '添加好友搜索框的搜索结果页' },
  1043: { id: 1043, description: '公众号模板消息' },
  1044: { id: 1044, description: '带 shareTicket 的小程序消息卡片' },
  1045: { id: 1045, description: '朋友圈广告' },
  1046: { id: 1046, description: '朋友圈广告详情页' },
  1047: { id: 1047, description: '扫描小程序码' },
  1048: { id: 1048, description: '长按图片识别小程序码' },
  1049: { id: 1049, description: '扫描手机相册中选取的小程序码' },
  1052: { id: 1052, description: '卡券的适用门店列表' },
  1053: { id: 1053, description: '搜一搜的结果页' },
  1054: { id: 1054, description: '顶部搜索框小程序快捷入口', deprecated: true },
  1056: { id: 1056, description: '聊天顶部音乐播放器右上角菜单' },
  1057: { id: 1057, description: '钱包中的银行卡详情页' },
  1058: { id: 1058, description: '公众号文章' },
  1059: { id: 1059, description: '体验版小程序绑定邀请页' },
  1060: { id: 1060, description: '微信支付完成页' },
  1064: { id: 1064, description: '微信首页连Wi-Fi状态栏' },
  1065: { id: 1065, description: 'URL scheme' },
  1067: { id: 1067, description: '公众号文章广告' },
  1068: { id: 1068, description: '附近小程序列表广告', deprecated: true },
  1069: { id: 1069, description: '移动应用通过openSDK进入微信，打开小程序' },
  1071: { id: 1071, description: '钱包中的银行卡列表页' },
  1072: { id: 1072, description: '二维码收款页面' },
  1073: { id: 1073, description: '客服消息列表下发的小程序消息卡片' },
  1074: { id: 1074, description: '公众号会话下发的小程序消息卡片' },
  1077: { id: 1077, description: '摇周边' },
  1078: { id: 1078, description: '微信连Wi-Fi成功提示页' },
  1079: { id: 1079, description: '微信游戏中心' },
  1081: { id: 1081, description: '客服消息下发的文字链' },
  1082: { id: 1082, description: '公众号会话下发的文字链' },
  1084: { id: 1084, description: '朋友圈广告原生页' },
  1088: { id: 1088, description: '会话中查看系统消息，打开小程序' },
  1089: { id: 1089, description: '微信聊天主界面下拉，「最近使用」栏' },
  1090: { id: 1090, description: '长按小程序右上角菜单唤出最近使用历史' },
  1091: { id: 1091, description: '公众号文章商品卡片' },
  1092: { id: 1092, description: '城市服务入口' },
  1095: { id: 1095, description: '小程序广告组件' },
  1096: { id: 1096, description: '聊天记录，打开小程序' },
  1097: { id: 1097, description: '微信支付签约原生页，打开小程序' },
  1099: { id: 1099, description: '页面内嵌插件' },
  1100: { id: 1100, description: '红包封面详情页打开小程序' },
  1101: { id: 1101, description: '远程调试热更新' },
  1102: { id: 1102, description: '公众号 profile 页服务预览' },
  1103: { id: 1103, description: '发现页小程序「我的小程序」列表', deprecated: true },
  1104: { id: 1104, description: '微信聊天主界面下拉，「我的小程序」栏', deprecated: true },
  1106: { id: 1106, description: '聊天主界面下拉，从顶部搜索结果页，打开小程序' },
  1107: { id: 1107, description: '订阅消息，打开小程序' },
  1113: { id: 1113, description: '安卓手机负一屏，打开小程序（三星）' },
  1114: { id: 1114, description: '安卓手机侧边栏，打开小程序（三星）' },
  1119: { id: 1119, description: '【企业微信】工作台内打开小程序' },
  1120: { id: 1120, description: '【企业微信】个人资料页内打开小程序' },
  1121: { id: 1121, description: '【企业微信】聊天加号附件框内打开小程序' },
  1124: { id: 1124, description: '扫"一物一码"打开小程序' },
  1125: { id: 1125, description: '长按图片识别"一物一码"' },
  1126: { id: 1126, description: '扫描手机相册中选取的"一物一码"' },
  1129: { id: 1129, description: '微信爬虫访问' },
  1131: { id: 1131, description: '浮窗（8.0版本起仅包含被动浮窗）' },
  1133: { id: 1133, description: '硬件设备打开小程序' },
  1135: { id: 1135, description: '小程序profile页相关小程序列表，打开小程序' },
  1144: { id: 1144, description: '公众号文章 - 视频贴片' },
  1145: { id: 1145, description: '发现栏 - 发现小程序' },
  1146: { id: 1146, description: '地理位置信息打开出行类小程序' },
  1148: { id: 1148, description: '卡包-交通卡，打开小程序' },
  1150: { id: 1150, description: '扫一扫商品条码结果页打开小程序' },
  1151: { id: 1151, description: '发现栏 - 我的订单' },
  1152: { id: 1152, description: '订阅号视频打开小程序' },
  1153: { id: 1153, description: '"识物"结果页打开小程序' },
  1154: { id: 1154, description: '朋友圈内打开"单页模式"' },
  1155: { id: 1155, description: '"单页模式"打开小程序' },
  1157: { id: 1157, description: '服务号会话页打开小程序' },
  1158: { id: 1158, description: '群工具打开小程序' },
  1160: { id: 1160, description: '群待办' },
  1167: { id: 1167, description: 'H5 通过开放标签打开小程序' },
  1168: { id: 1168, description: '移动/网站应用直接运行小程序' },
  1169: { id: 1169, description: '发现栏小程序主入口，各个生活服务入口' },
  1171: { id: 1171, description: '微信运动记录（仅安卓）' },
  1173: { id: 1173, description: '聊天素材用小程序打开' },
  1175: { id: 1175, description: '视频号主页商店入口' },
  1176: { id: 1176, description: '视频号直播间主播打开小程序' },
  1177: { id: 1177, description: '视频号直播商品' },
  1178: { id: 1178, description: '在电脑打开手机上打开的小程序' },
  1179: { id: 1179, description: '#话题页打开小程序' },
  1181: { id: 1181, description: '网站应用打开PC小程序' },
  1183: { id: 1183, description: 'PC微信 - 小程序面板 - 发现小程序 - 搜索' },
  1184: { id: 1184, description: '视频号链接打开小程序' },
  1185: { id: 1185, description: '群公告' },
  1186: { id: 1186, description: '收藏 - 笔记' },
  1187: { id: 1187, description: '浮窗（8.0版本起）' },
  1189: { id: 1189, description: '表情雨广告' },
  1191: { id: 1191, description: '视频号活动' },
  1192: { id: 1192, description: '企业微信联系人profile页' },
  1193: { id: 1193, description: '视频号主页服务菜单打开小程序' },
  1194: { id: 1194, description: 'URL Link' },
  1195: { id: 1195, description: '视频号主页商品tab' },
  1196: { id: 1196, description: '个人状态打开小程序' },
  1197: { id: 1197, description: '视频号主播从直播间返回小游戏' },
  1198: { id: 1198, description: '视频号开播界面打开小游戏' },
  1200: { id: 1200, description: '视频号广告打开小程序' },
  1201: { id: 1201, description: '视频号广告详情页打开小程序' },
  1202: { id: 1202, description: '企微客服号会话打开小程序卡片' },
  1203: { id: 1203, description: '微信小程序压测工具的请求' },
  1206: { id: 1206, description: '视频号小游戏直播间打开小游戏' },
  1207: { id: 1207, description: '企微客服号会话打开小程序文字链' },
  1208: { id: 1208, description: '聊天打开商品卡片' },
  1212: { id: 1212, description: '青少年模式申请页打开小程序' },
  1215: { id: 1215, description: '广告预约打开小程序' },
  1216: { id: 1216, description: '视频号订单中心打开小程序' },
  1218: { id: 1218, description: '微信键盘预览打开小程序' },
  1219: { id: 1219, description: '视频号直播间小游戏一键上车' },
  1220: { id: 1220, description: '发现页设备卡片打开小程序' },
  1223: { id: 1223, description: '安卓桌面Widget打开小程序' },
  1225: { id: 1225, description: '音视频通话打开小程序' },
  1226: { id: 1226, description: '聊天消息在设备打开后打开小程序' },
  1228: { id: 1228, description: '视频号原生广告组件打开小程序' },
  1230: { id: 1230, description: '订阅号H5广告进入小程序' },
  1231: { id: 1231, description: '动态消息提醒入口打开小程序' },
  1232: { id: 1232, description: '搜一搜竞价广告打开小程序' },
  1233: { id: 1233, description: '小程序搜索页人气游戏模块打开小游戏' },
  1238: { id: 1238, description: '看一看信息流广告打开小程序' },
  1242: { id: 1242, description: '小程序发现页门店快送模块频道页进入小程序' },
  1244: { id: 1244, description: '#tag搜索结果页打开小程序' },
  1245: { id: 1245, description: '小程序发现页门店快送搜索结果页进入小程序' },
  1248: { id: 1248, description: '通过小程序账号迁移进入小程序' },
  1252: { id: 1252, description: '搜一搜小程序搜索页「小功能」模块进入小程序' },
  1254: { id: 1254, description: '发现页「动态」卡片 打开小程序' },
  1255: { id: 1255, description: '发现页「我的」卡片 打开小程序' },
  1256: { id: 1256, description: 'pc端小程序面板「最近使用」列表' },
  1257: { id: 1257, description: 'pc端小程序面板「我的小程序」列表' },
  1258: { id: 1258, description: 'pc端小程序面板「为电脑端优化」模块' },
  1259: { id: 1259, description: 'pc端小程序面板「小游戏专区」模块' },
  1260: { id: 1260, description: 'pc端小程序面板「推荐在电脑端使用」列表' },
  1261: { id: 1261, description: '公众号返佣商品卡片' },
  1265: { id: 1265, description: '小程序图片详情页打开小程序' },
  1266: { id: 1266, description: '小程序图片长按半屏入口打开小程序' },
  1267: { id: 1267, description: '小程序图片会话角标打开小程序' },
  1271: { id: 1271, description: '微信聊天主界面下拉，「我的常用小程序」栏' },
  1272: { id: 1272, description: '发现页「游戏」服务tab打开小程序' },
  1273: { id: 1273, description: '发现页「常用的小程序」列表' },
  1278: { id: 1278, description: '发现页「发现小程序」列表打开小程序' },
  1279: { id: 1279, description: '发现页「发现小程序」合集页打开小程序' },
  1280: { id: 1280, description: '下拉任务栏小程序垂搜「建议使用」打开小程序' },
  1281: { id: 1281, description: '下拉任务栏小程序垂搜「发现小程序」打开小程序' },
  1282: { id: 1282, description: '听一听播放器打开小程序' },
  1285: { id: 1285, description: '发现页「发现小程序」短剧合集打开小程序' },
  1286: { id: 1286, description: '明文scheme打开小程序' },
  1287: { id: 1287, description: '公众号短剧贴片打开小程序' },
  1292: { id: 1292, description: '发现页「发现小程序」poi 详情页打开小程序' },
  1293: { id: 1293, description: '发现页短剧卡片追剧页打开小程序' },
  1295: { id: 1295, description: '下拉任务栏小程序垂搜「发现小程序」广告打开小程序' },
  1296: { id: 1296, description: '视频号付费短剧气泡打开小程序' },
  1297: { id: 1297, description: '发现-小程序-搜索「发现小程序」打开小程序' },
  1298: { id: 1298, description: '下拉任务栏小程序垂搜「发现小程序」打开的合集访问小程序' },
  1299: { id: 1299, description: '下拉任务栏小程序垂搜「发现小程序」poi 详情页打开小程序' },
  1300: { id: 1300, description: '发现-小程序-搜索「发现小程序」打开的合集访问小程序' },
  1301: { id: 1301, description: '发现-小程序-搜索「发现小程序」poi 详情页打开小程序' },
  1302: { id: 1302, description: 'PC端面板「发现小程序」' },
  1303: { id: 1303, description: '发现页短剧卡片视频流打开小程序' },
  1304: { id: 1304, description: '手机负一屏打开小程序（比如oppo手机）' },
  1305: { id: 1305, description: '公众号播放结束页打开小程序' },
  1306: { id: 1306, description: '公众号短剧固定选集入口打开小程序' },
  1307: { id: 1307, description: '发现页附近服务境外专区打开小程序' },
  1308: { id: 1308, description: 'PC端面板小游戏专区页面' },
  1309: { id: 1309, description: '公众号文章打开小游戏CPS卡片' },
};

/** 获取场景值描述 */
export const getSceneDescription = (sceneId: number): string => {
  return wechatMpSceneConfig[sceneId]?.description || '未知场景';
};

/** 判断场景值是否已废弃 */
export const isSceneDeprecated = (sceneId: number): boolean => {
  return wechatMpSceneConfig[sceneId]?.deprecated || false;
};

/** 获取所有场景值 */
export const getAllScenes = (): WechatMpScene[] => {
  return Object.values(wechatMpSceneConfig);
};

/** 获取所有未废弃的场景值 */
export const getActiveScenes = (): WechatMpScene[] => {
  return getAllScenes().filter((scene) => !scene.deprecated);
};
